import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { useFeatureFlag } from '@/shared/hooks/useFeatureFlag';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { ConfirmDialog } from '@/shared/ui/Modal';
import styles from './CaptureTab.module.css';
import smsStyles from '../sms/TextRequests.module.css';
import { CaptureStats } from './CaptureStats';
import { ManualCaptureCard } from './ManualCaptureCard';
import { HowItWorks } from './HowItWorks';
import { PhonePreview } from './PhonePreview';
import { SubmissionsSection } from './SubmissionsSection';
import { TextRequestsTab } from '../sms/TextRequestsTab';
import { ConversationsTab } from '../sms/ConversationsTab';
import { ReviewsSummaryStrip } from '../analytics/ReviewsSummaryStrip';
import { useCanEditProgram, useSmsProgram } from '../sms/hooks';
import type { RequestMethod } from '../api/reviewSmsApi';

/**
 * Get reviews: how the practice asks patients for a review. Text after the visit is one way to do
 * it, so it lives here rather than in its own tab. One method at a time (either/or): the link and
 * QR code, or Betty texting patients after their confirmed visits. Without the reviewsSms flag
 * there is only the link and QR code.
 */
export function CaptureTab() {
  const textAvailable = useFeatureFlag('review-sms');
  return textAvailable ? <CaptureWithMethod /> : <LinkMethod />;
}

function LinkMethod() {
  return (
    <div className={styles.root}>
      <CaptureStats />
      <div className={styles.splitLayout}>
        <div className={styles.leftCol}>
          <ManualCaptureCard />
          <HowItWorks />
        </div>
        <div className={styles.rightCol}>
          <PhonePreview />
        </div>
      </div>
      <SubmissionsSection />
    </div>
  );
}

const METHODS: Array<{ value: RequestMethod; title: string; hint: string; isNew?: boolean }> = [
  {
    value: 'link',
    title: 'Link & QR code',
    hint: 'Show the QR code at checkout, print it on receipts, or send the link yourself. Patients start the review on their phone.',
  },
  {
    value: 'text',
    title: 'Text after the visit',
    hint: 'Betty texts each patient after their confirmed visit and helps them write the review right in the text thread.',
    isNew: true,
  },
];

function CaptureWithMethod() {
  const program = useSmsProgram();
  const canEdit = useCanEditProgram();
  const [confirmLink, setConfirmLink] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  if (program.isLoading) return <SkeletonCard />;
  if (program.isError || !program.data) {
    // Never guess "link" for a practice that may be texting: say so, keep the link tools usable.
    return (
      <div className={styles.root}>
        <Callout
          variant="error"
          title="Couldn’t load your texting settings."
          actions={
            <Button size="sm" variant="secondary" onClick={() => void program.refetch()}>
              Try again
            </Button>
          }
        >
          The link and QR code below still work.
        </Callout>
        <LinkMethod />
      </div>
    );
  }
  const view = program.data;
  // Mirrors the server's effectiveRequestMethod for a backend that predates the field.
  const method: RequestMethod =
    view.requestMethod ?? (view.status === 'not_started' ? 'link' : 'text');
  // On the link, threads that started while texting can still need staff (alerts link here).
  const showConversations = method === 'link' && searchParams.get('sub') === 'conversations';
  const setConversationParams = (sub: string | null, conversation?: string | null) =>
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (sub) params.set('sub', sub);
      else params.delete('sub');
      if (conversation) params.set('conversation', conversation);
      else params.delete('conversation');
      return params;
    });
  const textingActive = view.status === 'live' || view.status === 'awaiting_registration';

  const choose = (next: RequestMethod) => {
    if (next === method || !canEdit) return;
    if (next === 'link' && textingActive) {
      setConfirmLink(true);
      return;
    }
    program.setMethod.mutate(next);
  };

  return (
    <div className={styles.root}>
      <ReviewsSummaryStrip
        onFollowUp={() =>
          method === 'text'
            ? setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set('sub', 'conversations');
                return params;
              })
            : setConversationParams('conversations')
        }
      />
      <div>
        <div className={smsStyles.stepHeader}>
          <h3 className={smsStyles.stepHeading}>How should we ask patients for reviews?</h3>
          <span className={smsStyles.hint}>One method at a time</span>
        </div>
        <div className={smsStyles.choice} role="radiogroup" aria-label="How to ask for reviews">
          {METHODS.map((m) => (
            <button
              key={m.value}
              type="button"
              role="radio"
              aria-checked={method === m.value}
              disabled={!canEdit || program.setMethod.isPending}
              className={clsx(smsStyles.option, method === m.value && smsStyles.optionActive)}
              onClick={() => choose(m.value)}
            >
              <span className={smsStyles.optionTitle}>
                {m.title}
                {m.isNew && <span className={smsStyles.newTag}>New</span>}
              </span>
              <span className={smsStyles.hint}>{m.hint}</span>
            </button>
          ))}
        </div>
        <p className={smsStyles.hint}>
          Switching to the link pauses texting; switching back picks up where you left off. QR codes
          you already printed keep working either way.
        </p>
      </div>

      {method === 'text' && <TextRequestsTab />}
      {method === 'link' && showConversations && (
        <div className={styles.root}>
          <div>
            <Button variant="ghost" size="sm" onClick={() => setConversationParams(null)}>
              ← Back to link & QR code
            </Button>
          </div>
          <ConversationsTab
            locationId={program.locationId}
            timezone={view.timing.practiceTimezone}
            selectedId={searchParams.get('conversation')}
            onSelect={(id) => setConversationParams('conversations', id)}
          />
        </div>
      )}
      {method === 'link' && !showConversations && (
        <>
          {view.liveSince && (
            <p className={smsStyles.hint}>
              Text conversations from before the switch still answer patients.{' '}
              <button
                type="button"
                className={smsStyles.pillLink}
                onClick={() => setConversationParams('conversations')}
              >
                View text conversations →
              </button>
            </p>
          )}
          <LinkMethod />
        </>
      )}

      <ConfirmDialog
        isOpen={confirmLink}
        onClose={() => setConfirmLink(false)}
        onConfirm={() => {
          setConfirmLink(false);
          program.setMethod.mutate('link');
        }}
        title="Switch to the link and QR code?"
        message="Betty stops texting patients. Conversations already in progress still answer STOP and HELP, and you can switch back any time."
        confirmLabel="Switch to link & QR code"
        isLoading={program.setMethod.isPending}
      />
    </div>
  );
}
